import {
	createContext,
	useContext,
	useCallback,
	useState,
	useEffect,
	type ReactNode,
} from "react";
import { getPhotos } from "../lib/api";
import type { Photo } from "../components/gallery/types";

const SERVER_URL = import.meta.env.VITE_API_URL;

interface HeaderImagesContextType {
	visibleImages: Set<number>;
	images: string[];
	registerImage: (index: number) => void;
	unregisterImage: (index: number) => void;
	getNextAvailableImage: (currentIndex: number) => number;
}

const HeaderImagesContext = createContext<HeaderImagesContextType | null>(null);

export const useHeaderImages = () => {
	const context = useContext(HeaderImagesContext);
	if (!context) {
		throw new Error("useHeaderImages must be used within HeaderImagesProvider");
	}
	return context;
};

interface HeaderImagesProviderProps {
	children: ReactNode;
}

export const HeaderImagesProvider = ({
	children,
}: HeaderImagesProviderProps) => {
	const [visibleImages, setVisibleImages] = useState<Set<number>>(new Set());
	const [images, setImages] = useState<string[]>([]);

	// Načtení obrázků při prvním mountu
	useEffect(() => {
		const loadImages = async () => {
			try {
				const photos = await getPhotos();
				const photosWithFullUrl = Array.isArray(photos)
					? photos.map((photo: Photo) => ({
							...photo,
							url: photo.url.startsWith("http")
								? photo.url
								: `${SERVER_URL}/${photo.url}`,
						}))
					: [];

				// Statické fotky jako záloha
				const staticImages = [
					"/images/header/foto1.jpg",
					"/images/header/foto2.jpg",
					"/images/header/foto3.jpg",
					"/images/header/foto4.jpg",
					"/images/header/foto5.jpg",
					"/images/header/foto6.jpg",
					"/images/header/foto7.jpg",
					"/images/header/foto8.jpg",
					"/images/header/foto9.jpg",
					"/images/header/foto10.jpg",
				];

				// Vybereme náhodné fotky z galerie
				const shuffled = [...photosWithFullUrl].sort(() => 0.5 - Math.random());
				const selected = shuffled.slice(0, 5).map((photo) => photo.url);

				// Kombinujeme náhodné fotky s některými statickými
				const combinedImages = [...selected, ...staticImages];
				// Zamícháme výsledný seznam
				const finalImages = combinedImages.sort(() => 0.5 - Math.random());
				setImages(finalImages);
			} catch (error) {
				console.error("Chyba při načítání fotek:", error);
				// Fallback na statické fotky v případě chyby
				setImages([
					"/images/header/foto1.jpg",
					"/images/header/foto2.jpg",
					"/images/header/foto3.jpg",
					"/images/header/foto4.jpg",
					"/images/header/foto5.jpg",
					"/images/header/foto6.jpg",
					"/images/header/foto7.jpg",
					"/images/header/foto8.jpg",
					"/images/header/foto9.jpg",
					"/images/header/foto10.jpg",
				]);
			}
		};

		loadImages();
	}, []);

	const registerImage = useCallback((index: number) => {
		setVisibleImages((prev) => new Set([...prev, index]));
	}, []);

	const unregisterImage = useCallback((index: number) => {
		setVisibleImages((prev) => {
			const next = new Set(prev);
			next.delete(index);
			return next;
		});
	}, []);

	const getNextAvailableImage = useCallback(
		(currentIndex: number): number => {
			const totalImages = images.length;

			// Nejprve zkusíme náhodný index
			const randomIndex = Math.floor(Math.random() * totalImages);
			if (!visibleImages.has(randomIndex) && randomIndex !== currentIndex) {
				return randomIndex;
			}

			// Pokud je náhodný index obsazený, zkusíme postupně další
			for (let i = 0; i < totalImages; i++) {
				const nextIndex = (randomIndex + i) % totalImages;
				if (!visibleImages.has(nextIndex) && nextIndex !== currentIndex) {
					return nextIndex;
				}
			}

			// Pokud jsou všechny obsazené, vrátíme náhodný
			return Math.floor(Math.random() * totalImages);
		},
		[images.length, visibleImages],
	);

	const value = {
		visibleImages,
		images,
		registerImage,
		unregisterImage,
		getNextAvailableImage,
	};

	return (
		<HeaderImagesContext.Provider value={value}>
			{children}
		</HeaderImagesContext.Provider>
	);
};
